/**
 * The clipboard serializer — `KLQ-20260824-002`.
 *
 * Kalqa replaces a Google-Sheets-and-PDF workflow, and the cheapest adoption bridge is the
 * clipboard: what is selected here lands in a sheet as cells, not as a paragraph. So this
 * module turns a selection into the two flavours a spreadsheet understands — `text/plain` as
 * **TSV**, which Sheets and Excel parse natively, and `text/html` as a `<table>`, which rich
 * targets use to keep the columns — and it does nothing else.
 *
 * **It is pure, and it is not the table.** Nothing here reads the DOM, the selection context
 * or a query. A caller hands it what the table drew and what is selected; it hands back two
 * strings. That is what makes the awkward part — which rectangle, which header, which
 * escaping — testable without rendering anything, and it is why the ownership split of
 * `KLQ-20260825-006` (which cells) and `KLQ-20260828-001` (what a cell is worth) leaves this
 * file with exactly one job.
 *
 * **The block is always a rectangle.** A person can hold Cmd and tick nine scattered cells;
 * a sheet has no way to receive that. So the payload is the bounding box of the selection,
 * and the cells inside it that were not selected are written as empty — a grid with holes,
 * rather than a jagged thing the receiving sheet would silently re-flow into the wrong
 * columns.
 */

/** What the table drew, in rendered order, with the two lookups the serializer needs. */
export interface GridSource {
  readonly rowIds: readonly string[];
  /** Data columns only, in displayed order. The select column never appears here. */
  readonly colKeys: readonly string[];
  readonly headerOf: (colKey: string) => string;
  /**
   * One cell as text. `undefined` when the column declares no `value()` — the column is part
   * of the rectangle (a sheet needs the gap) but has nothing to say about itself.
   */
  readonly valueOf: (rowId: string, colKey: string) => string | undefined;
  /**
   * **Where this cell's thing lives, absolute** — `undefined` for a cell that is not an
   * address (owner note, 2026-09-10: *"a copied task in a sheet is just `4.NP`, there is no
   * link back to it"*).
   *
   * The URL rides in the **HTML flavour only**, as an `<a href>` around the same text the TSV
   * carries. A sheet takes the HTML and the cell becomes clickable while still reading as the
   * name; a plain-text target takes the TSV and reads as the name too. The alternative — an
   * `=HYPERLINK(…)` in the TSV — was declined by the owner the same day: it is unreadable
   * anywhere that is not a spreadsheet, and Excel's argument separator follows the machine's
   * locale, so the one form that works in `en-US` is broken in `sk` and `de`.
   *
   * Absolute, because the destination of the paste is somebody else's document. A path would
   * resolve against whatever the sheet thinks its own origin is, which is nothing.
   */
  readonly linkOf?: (rowId: string, colKey: string) => string | undefined;
}

export interface ClipboardPayload {
  readonly text: string;
  readonly html: string;
}

/** One cell of the block: what it says, and where it points if it points anywhere. */
interface Cell {
  readonly text: string;
  readonly href: string | undefined;
}

/** A cell inside the rectangle that was not selected — a gap the sheet still needs. */
const BLANK: Cell = { text: "", href: undefined };

/**
 * TSV quoting, the way a spreadsheet expects it: a value containing a tab, a newline or a
 * quote is wrapped in quotes and its own quotes are doubled. Without this a task title with a
 * line break in it silently becomes two rows in the receiving sheet, shifted one column left.
 */
function tsvCell(value: string): string {
  if (!/["\t\n\r]/.test(value)) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

function htmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

/**
 * An `href` fit to paste into somebody else's document.
 *
 * **Only `http:` and `https:` survive**, and anything else — a relative path, a `javascript:`,
 * a string that is not a URL at all — is dropped and the cell is written as plain text. Every
 * link this module emits today is built by the app's own router, so the check buys nothing
 * *now*; it is here because the output is HTML that lands in a document nobody in this
 * codebase controls, and the day a link is built from a field somebody typed is not the day to
 * start thinking about it.
 */
function safeHref(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  return parsed.toString().replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function htmlCell(value: string, href: string | undefined): string {
  const text = htmlText(value);
  if (href === undefined || value === "") return text;
  const safe = safeHref(href);
  return safe === null ? text : `<a href="${safe}">${text}</a>`;
}

/**
 * The selection, as a block.
 *
 * Returns `null` when there is nothing to copy, so a caller can leave the browser's own copy
 * alone rather than overwriting the clipboard with an empty string — which is what makes
 * Cmd+C over a text selection elsewhere on the page keep working.
 */
export function serializeSelection(
  source: GridSource,
  isSelected: (rowId: string, colKey: string) => boolean,
): ClipboardPayload | null {
  const rowHits: number[] = [];
  const colHits: number[] = [];

  source.rowIds.forEach((rowId, r) => {
    source.colKeys.forEach((colKey, c) => {
      if (!isSelected(rowId, colKey)) return;
      rowHits.push(r);
      colHits.push(c);
    });
  });

  if (rowHits.length === 0) return null;

  const r1 = Math.min(...rowHits);
  const r2 = Math.max(...rowHits);
  const c1 = Math.min(...colHits);
  const c2 = Math.max(...colHits);

  // The header comes along when the block is *about* whole rows or whole columns — copying
  // one status cell into a sheet and getting a "State" heading above it would be noise, while
  // copying the register and getting no headings is the thing people then type by hand.
  const wholeRows = c1 === 0 && c2 === source.colKeys.length - 1;
  const wholeColumns = r1 === 0 && r2 === source.rowIds.length - 1;
  const withHeader = wholeRows || wholeColumns;

  const cols = source.colKeys.slice(c1, c2 + 1);
  const rows = source.rowIds.slice(r1, r2 + 1);

  // The two flavours diverge by exactly one field, so the block is built once carrying both:
  // a second pass keyed by row and column index is how the link ends up one cell off the value
  // it belongs to.
  const matrix: Cell[][] = rows.map((rowId) =>
    cols.map((colKey) => {
      if (!isSelected(rowId, colKey)) return BLANK;
      return {
        text: source.valueOf(rowId, colKey) ?? "",
        href: source.linkOf?.(rowId, colKey),
      };
    }),
  );

  // A heading is not an address, so it never carries one — `undefined` here rather than a
  // `withHeader` test further down.
  const headers: Cell[] = cols.map((colKey) => ({
    text: source.headerOf(colKey),
    href: undefined,
  }));
  const lines = withHeader ? [headers, ...matrix] : matrix;

  const text = lines
    .map((line) => line.map((cell) => tsvCell(cell.text)).join("\t"))
    .join("\n");

  const body = lines
    .map((line, index) => {
      const tag = withHeader && index === 0 ? "th" : "td";
      const cells = line
        .map((cell) => `<${tag}>${htmlCell(cell.text, cell.href)}</${tag}>`)
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return { text, html: `<table>${body}</table>` };
}

/**
 * Both flavours onto the clipboard, with the plain-text one as the fallback.
 *
 * `ClipboardItem` is what carries two MIME types at once; where it is missing, or where the
 * page is not a secure context, TSV alone still pastes into a sheet correctly — the HTML
 * flavour only buys nicer borders in a rich target. Failures are reported to the caller
 * rather than swallowed, because a copy that silently did nothing is the bug people file as
 * "the table is broken".
 */
export async function writeClipboard(
  payload: ClipboardPayload,
): Promise<boolean> {
  if (!navigator.clipboard || !window.isSecureContext) return false;
  try {
    if (typeof ClipboardItem === "function") {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([payload.text], { type: "text/plain" }),
          "text/html": new Blob([payload.html], { type: "text/html" }),
        }),
      ]);
      return true;
    }
    await navigator.clipboard.writeText(payload.text);
    return true;
  } catch {
    return false;
  }
}

/**
 * A clipboard payload, back to a grid — the read half of the bridge.
 *
 * TSV is what a spreadsheet puts on the clipboard as `text/plain`, and its quoting is the
 * awkward part: a quoted field may contain tabs, newlines and doubled quotes, so the parse
 * cannot be `split("\t")` per line. It is a small state machine instead, and it is the reason
 * this lives beside the serializer rather than at a call site — a copy that round-trips
 * through its own parser is a property worth being able to test.
 *
 * `\r\n` is normalised on the way in (Excel on Windows), and a trailing newline — which every
 * sheet adds — does not become an empty last row.
 */
export function parseClipboard(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  const endCell = (): void => {
    row.push(cell);
    cell = "";
  };
  const endRow = (): void => {
    endCell();
    rows.push(row);
    row = [];
  };

  const source = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < source.length; i += 1) {
    // `charAt`, not `source[i]`: the loop bound already guarantees a character, and this is
    // the form that says so to the type checker instead of asserting it.
    const char = source.charAt(i);
    if (quoted) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"' && cell === "") {
      quoted = true;
    } else if (char === "\t") {
      endCell();
    } else if (char === "\n") {
      endRow();
    } else {
      cell += char ?? "";
    }
  }
  // Whatever is left is the last cell, unless the text ended on its own newline.
  if (cell !== "" || row.length > 0) endRow();
  return rows;
}

/** The clipboard as text, or `null` where the browser will not give it. */
export async function readClipboard(): Promise<string | null> {
  if (!navigator.clipboard || !window.isSecureContext) return null;
  try {
    return await navigator.clipboard.readText();
  } catch {
    // A denied permission is a legitimate answer, not a crash: the person keeps the value
    // they had, and the table stays where it was.
    return null;
  }
}
